import { APP_HEADER_HEIGHT } from '@/constants/chat';
import { ChatLayoutContext } from '@/components/context/ChatLayoutContext';
import { CORPUS_QA, DATAVOYAGER, PAPER_FINDER } from 'configs/NoraVariants';
import { getCurrentThread } from '@/store/reducers/UIState';
import {
  mkAboutBtnTrackAttrs,
  mkBugReportBtnTrackAttrs,
  mkExpandBtnTrackAttrs,
} from '@/analytics/props/threadBar';
import { optAppConfig } from '@/utils/config';
import { ShareThreadButton } from '@/components/chat/ShareThreadButton';
import { useAppSelector } from '@/store/hooks';
import { useAuth0 } from '@/auth/Auth0Client';
import IconAstaLogoSmall from '@/components/icons/IconAstaLogoSmall';
import IconDataVoyagerLogoSmall from '@/components/icons/IconDataVoyagerLogoSmall';
import IconPaperFinderLogoSmall from '@/components/icons/IconPaperFinderLogoSmall';
import IconScholarQALogoSmall from '@/components/icons/IconScholarQALogoSmall';

import { Button, IconButton } from '@mui/material';
import { useContext } from 'react';
import classNames from 'classnames';
import MenuIcon from '@mui/icons-material/Menu';
import styled from 'styled-components';

type AppHeaderProps = {
  alwaysShowMenuButton?: boolean;
};

export const AppHeader = ({ alwaysShowMenuButton = false }: AppHeaderProps) => {
  const currentThread = useAppSelector(getCurrentThread);
  const { setIsChatDrawerOpen } = useContext(ChatLayoutContext);
  const { isAuthenticated } = useAuth0();

  const noraExperienceVariant = optAppConfig((_) => _.experience.variant);

  const getAboutLink = (): string => {
    switch (noraExperienceVariant) {
      case CORPUS_QA:
        return 'https://allenai.org/blog/ai2-scholarqa';
      case PAPER_FINDER:
        return 'https://allenai.org/blog/paper-finder';
      case DATAVOYAGER:
        return 'https://allenai.org/blog/data-driven-discovery-with-large-generative-models-e1a062e99390';
      default:
        return 'https://allenai.org/blog/ai2-scholarqa';
    }
  };

  const toolLogo =
    noraExperienceVariant === CORPUS_QA ? (
      <IconScholarQALogoSmall />
    ) : noraExperienceVariant === PAPER_FINDER ? (
      <IconPaperFinderLogoSmall />
    ) : noraExperienceVariant === DATAVOYAGER ? (
      <IconDataVoyagerLogoSmall />
    ) : (
      <IconAstaLogoSmall />
    );

  const onOpenChatDrawer = () => setIsChatDrawerOpen(true);

  return (
    <Header
      className={classNames({
        header__home: !currentThread,
      })}
    >
      <MenuAndLogo>
        <MenuButton
          {...mkExpandBtnTrackAttrs()}
          onClick={onOpenChatDrawer}
          $isAlwaysVisible={alwaysShowMenuButton}
        >
          <MenuIcon />
        </MenuButton>
        {!!currentThread && <a href="/">{toolLogo}</a>}
      </MenuAndLogo>
      <NavBar>
        <a target="_blank" rel="noreferrer" href={getAboutLink()}>
          <Button variant="text" {...mkAboutBtnTrackAttrs()}>
            About
          </Button>
        </a>
        <a
          target="_blank"
          rel="noreferrer"
          href="https://docs.google.com/forms/d/e/1FAIpQLSeEXy9unYmpL278qXZ8-EAL_p7PtuNY50aLNvWsXntuf3VJwQ/viewform?usp=sf_link&entry.268806865=/"
        >
          <Button variant="text" {...mkBugReportBtnTrackAttrs()}>
            Feedback
          </Button>
        </a>
        {isAuthenticated && !!currentThread && (
          <ShareThreadButton threadId={currentThread.id} />
        )}
      </NavBar>
    </Header>
  );
};

const Header = styled('div')`
  align-content: space-between;
  border-width: 0 0 1px 0;
  border-style: solid;
  border-color: ${({ theme }) => theme.color['dark-teal-100'].hex};
  display: flex;
  flex-wrap: wrap;
  grid-gap: ${({ theme }) => theme.spacing(1)};
  height: ${APP_HEADER_HEIGHT * 2}px;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing(1.5)};

  &.header__home {
    height: ${APP_HEADER_HEIGHT}px;
  }

  & > a,
  button {
    color: ${({ theme }) => theme.palette.secondary.main};
  }

  @media only screen and (min-width: 600px) {
    align-content: center;
    height: ${APP_HEADER_HEIGHT}px;
    padding: 0 ${({ theme }) => theme.spacing(3)};
    &.header__home {
      flex-direction: row;
    }
  }
`;

const MenuButton = styled(IconButton)<{ $isAlwaysVisible: boolean }>`
  &.MuiIconButton-root {
    border-radius: 40px;

    &:hover {
      background: rgba(250, 242, 233, 0.04);
    }

    // Hide the menu button on larger screens unless alwaysShowMenuButton is true
    @media only screen and (min-width: 600px) {
      display: ${({ $isAlwaysVisible }) =>
        $isAlwaysVisible ? 'block' : 'none'};
    }
  }
`;

const MenuAndLogo = styled('div')`
  display: flex;
  flex-grow: 1;

  a {
    display: flex;
    align-items: center;
  }

  @media only screen and (min-width: 600px) {
    gap: ${({ theme }) => theme.spacing(4)};
    flex-direction: row;
    justify-content: flex-start;
    width: auto;
    padding-left: 0;
  }
`;

const NavBar = styled('div')`
  align-items: center;
  display: flex;
`;
